import { createTheme } from '@mui/material/styles';

import AllClientViewRow from './AllClientViewRow';
import SingleClientViewRow from './SingleClientsViewRow';
import { primaryTheme } from '../../../utils/theme';
import { FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import SortImg from '../../../assets/images/sort.svg';

export const allClientsViewOptions = {
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: true,
  download: false,
  responsive: 'standard' as const,
  selectableRows: 'none' as const,
  textLabels: {
    body: {
      noMatch: 'No records match the selected criteria',
    },
  },
  customRowRender: (data: any[]) => {
    const [id, name, sessionHours, clientSNHours, csnSnRatio, parentSNHours, caHours, gpsnHours] =
      data;

    return (
      <AllClientViewRow
        id={id}
        name={name}
        sessionHours={sessionHours}
        clientSNHours={clientSNHours}
        csnSnRatio={csnSnRatio}
        parentSNHours={parentSNHours}
        caHours={caHours}
        gpsnHours={gpsnHours}
      />
    );
  },
};

export const allClientsViewColumns = [
  {
    name: 'id',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
  {
    name: 'name',
    label: 'Client',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'session_duration',
    label: 'Sessions (53)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'client_supervision_duration',
    label: 'Client SN (55)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'csn_session_ratio',
    label: '55/53 ratio',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'parent_supervision_duration',
    label: 'Parent SN (56)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'client_assessment_duration',
    label: 'Ass. (51)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'group_parent_supervision_duration',
    label: 'Group SN (57)',
    options: {
      filter: false,
      sort: true,
    },
  },
];

export const singleClientsViewOptions = {
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: true,
  download: false,
  responsive: 'standard' as const,
  selectableRows: 'none' as const,
  textLabels: {
    body: {
      noMatch: 'No records',
    },
  },
  customRowRender: (data: any[]) => {
    const [id, startDate, endDate, period] = data;

    return <SingleClientViewRow id={id} startDate={startDate} endDate={endDate} period={period} />;
  },
};

export const getSingleClientsViewColumns = (period: string) => [
  {
    name: 'id',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
  {
    name: 'startDate',
    label: '',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
  {
    name: 'endDate',
    label: '',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
  {
    name: 'period',
    label: '',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
  {
    name: 'startDate',
    label: period === 'week' ? 'Week' : 'Month',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'sessionHours',
    label: 'Sessions (53)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'clientSNHours',
    label: 'Client SN (55)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'csnSnRatio',
    label: '55/53 ratio',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'parentSNHours',
    label: 'Parent SN (56)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'client_assessment_duration',
    label: 'Ass. (51)',
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'group_parent_supervision_duration',
    label: 'Group SN (57)',
    options: {
      filter: false,
      sort: true,
    },
  },
];

export const supervisionMetricsTableTheme = createTheme({
  ...primaryTheme,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: FORTA_BLUE.light3,
          boxShadow: 'none',
        },
        elevation2: {
          borderRadius: '4px',
          boxShadow: `0px 5px 5px ${FORTA_BLUE.light2}`,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          cursor: 'default',
          borderCollapse: 'separate',
          borderSpacing: '0 10px',
          '& caption': {
            display: 'none',
          },
          textAlign: 'left',
        },
      },
    },
    MUIDataTableFilter: {
      styleOverrides: {
        root: {},
        MuiGrid: {
          root: {
            flexDirection: 'column',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: FORTA_BLUE.light1,
          backgroundColor: FORTA_WHITE,
          border: `2px solid ${FORTA_BLUE.light3}`,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          textAlign: 'left',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h6: {
          fontSize: '21px !important',
          fontWeight: '700 !important',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        head: {
          borderRadius: 0,
          boxShadow: '0px 0px 0px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          textAlign: 'left',
          backgroundColor: FORTA_WHITE,
          borderWidth: 0,
          fontSize: '14px',
          '&:first-of-type': {
            borderLeftStyle: 'solid',
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          },
          '&:last-of-type': {
            borderRightStyle: 'solid',
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
          },
        },
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: FORTA_BLUE.light3,
          padding: '0 8px',
          fontWeight: 'bold',
        },
        contentWrapper: {
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          '::before': {
            content: `""`,
            backgroundImage: `url(${SortImg})`,
            backgroundRepeat: 'no-repeat',
            width: '1.2em',
            height: '1.2em',
            display: 'block',
            marginTop: '0.5em',
            marginRight: '4px',
          },
        },
        sortAction: {
          '& path': {
            color: FORTA_BLUE.primary,
          },
        },
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {},
      },
    },
  },
});
